/**
 * EditableWidgetCard — edit-mode chrome wrapping WidgetHost: a drag-handle +
 * WidgetOverflowMenu overlay pinned to the card's top-right (top:20px;
 * right:20px matches sectionCardStyle's own 20px padding, so it visually
 * sits flush in each widget's existing header row without prop-drilling a
 * "header extra" slot through all 9 Host components). The widget BODY is
 * `inert` while editing — not to gate dragging (RGL's own draggableHandle
 * already scopes drag origin to .widget-drag-handle) but to stop an
 * accidental activation inside a chart/table (mouse OR keyboard — e.g.
 * AccountsTable rows are tabIndex={0} with Enter/Space navigate() handlers)
 * from navigating away and silently discarding in-progress layout work.
 * `inert` (not pointerEvents:'none') blocks focus, pointer, and AT in one
 * attribute, which pointer-events alone does not — a keyboard user could
 * Tab past the kebab into a pointer-inert row and still activate it.
 */
import { WidgetHost } from './WidgetHost';
import { WidgetOverflowMenu } from './WidgetOverflowMenu';
import type { DashboardWidgetResponse } from '@/types/domain';

function GripIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" />
      <circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" />
      <circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
    </svg>
  );
}

export interface EditableWidgetCardProps {
  widget: DashboardWidgetResponse;
  accountId: string | null;
  row: number;
  col: number;
  onRename: () => void;
  onMoveResize: () => void;
  onRemove: () => void;
  /** M4 Configure surface — threaded straight through to WidgetOverflowMenu. */
  onConfigure?: () => void;
}

export function EditableWidgetCard({ widget, accountId, row, col, onRename, onMoveResize, onRemove, onConfigure }: EditableWidgetCardProps) {
  const title = widget.title ?? 'Widget';
  return (
    <div style={{ position: 'relative', height: '100%' }} role="group" aria-label={`${title} widget, row ${row} column ${col}`}>
      <div style={{ position: 'absolute', top: 20, right: 20, zIndex: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
        <span
          className="widget-drag-handle"
          aria-hidden="true"
          title="Drag to move"
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28,
            // 44px minimum touch target (design brief A11Y) via content-box
            // padding + a matching negative margin — the GhostIconButton
            // trick — so the visible 28px pill keeps its layout footprint.
            padding: 8, margin: -8, boxSizing: 'content-box', backgroundClip: 'content-box',
            borderRadius: 'var(--radius-pill)', cursor: 'grab', color: 'var(--text-subtle)',
            // backgroundColor (not the `background` shorthand) — the shorthand
            // resets background-clip to border-box when assigned after it in
            // insertion order (GhostIconButton.tsx's "QA R2 regression fix").
            backgroundColor: 'var(--surface-2)', touchAction: 'none',
          }}
        >
          <GripIcon />
        </span>
        <WidgetOverflowMenu widget={widget} onRename={onRename} onMoveResize={onMoveResize} onRemove={onRemove} onConfigure={onConfigure} />
      </div>
      <div style={{ height: '100%' }} inert>
        <WidgetHost widget={widget} accountId={accountId} />
      </div>
    </div>
  );
}
